import React, { useState, useEffect } from 'react'
import AppLayout from '../components/AppLayout'
import { useAuth } from '../contexts/AuthContext'
import { supabase, ugandaDate, ugandaMonth, ugandaDatetime } from '../lib/supabase'

export default function Requisitions() {
  const { user, profile, isAdminOrManager } = useAuth()
  const [tab, setTab] = useState('pending')
  const [reqs, setReqs] = useState([])
  const [lowStock, setLowStock] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState(null)
  const [form, setForm] = useState({ needed_by:'', urgency:'Normal', notes:'', items:[] })
  const [showForm, setShowForm] = useState(false)

  useEffect(() => { loadData() }, [tab])

  const loadData = async () => {
    setLoading(true)
    const [reqRes, stockRes] = await Promise.all([
      supabase.from('requisitions').select('*, profiles!requested_by(full_name), approver:profiles!approved_by(full_name)').order('created_at', { ascending:false }).limit(50),
      supabase.from('stock_balances').select('*').filter('needs_reorder', 'eq', true)
    ])
    setReqs(reqRes.data || [])
    setLowStock(stockRes.data || [])
    // Pre-populate form items from low stock
    if (stockRes.data?.length > 0) {
      setForm(f => ({
        ...f,
        items: stockRes.data.map(s => ({
          stock_item_id: s.id,
          item_name: s.name,
          current_balance: s.current_balance,
          reorder_level: s.reorder_level,
          reorder_qty: s.reorder_qty,
          qty_requested: s.reorder_qty || '',
          unit: s.unit,
          reason: `Stock at ${parseFloat(s.current_balance).toFixed(2)} ${s.unit} — below reorder level of ${s.reorder_level} ${s.unit}`
        }))
      }))
    }
    setLoading(false)
  }

  const setItem = (i, field, val) => {
    const items = [...form.items]
    items[i] = { ...items[i], [field]: val }
    setForm(f => ({ ...f, items }))
  }

  const nextReqNum = () => {
    const nums = reqs.map(r => parseInt(r.req_number?.replace(/\D/g,''))).filter(Boolean)
    const next = nums.length ? Math.max(...nums) + 1 : 1
    return `REQ-${new Date().getFullYear()}-${String(next).padStart(3,'0')}`
  }

  const handleSave = async () => {
    const validItems = form.items.filter(i => i.qty_requested)
    if (!validItems.length) { setMsg({ type:'error', text:'Please enter quantities for at least one item.' }); return }
    setSaving(true)
    const reqNumber = nextReqNum()
    const { error } = await supabase.from('requisitions').insert({
      requested_by: user.id,
      req_number: reqNumber,
      needed_by: form.needed_by || null,
      urgency: form.urgency,
      notes: form.notes,
      items: validItems,
      status: 'Pending',
      is_auto_generated: false,
    })
    if (error) { setMsg({ type:'error', text:error.message }); setSaving(false); return }

    // Send email notification via API
    try {
      await fetch('/api/notify-requisition', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          req_number: reqNumber,
          requested_by: profile?.full_name || user.email,
          urgency: form.urgency,
          needed_by: form.needed_by,
          items: validItems,
          notes: form.notes,
        })
      })
    } catch(e) { /* email failure should not block requisition save */ }

    setMsg({ type:'success', text:`Requisition ${reqNumber} submitted. Procurement, Edward & Sharon have been notified.` })
    setSaving(false); setShowForm(false); loadData()
    setTimeout(() => setMsg(null), 6000)
  }

  const [rejectModal, setRejectModal] = React.useState(null) // req being rejected
  const [rejectReason, setRejectReason] = React.useState('')

  const handleApprove = async (req, action) => {
    if (action === 'reject') {
      setRejectModal(req)
      setRejectReason('')
      return
    }
    await supabase.from('requisitions').update({ status:'Approved', approved_by:user.id, approved_at: new Date().toISOString() }).eq('id', req.id)
    loadData()
  }

  const handleConfirmReject = async () => {
    if (!rejectReason.trim()) return
    await supabase.from('requisitions').update({ status:'Rejected', approved_by:user.id, rejection_reason:rejectReason }).eq('id', rejectModal.id)
    setRejectModal(null); setRejectReason(''); loadData()
  }

  const handleConvertToPO = async (req) => {
    // Create a draft PO from this requisition
    const items = req.items.map(i => ({
      description: i.item_name,
      qty: i.qty_requested,
      unit: i.unit,
      unit_price: '',
      total: ''
    }))
    const { data: po, error } = await supabase.from('purchase_orders').insert({
      ordered_by: user.id,
      po_number: `PO-${new Date().getFullYear()}-${Date.now().toString().slice(-4)}`,
      order_date: ugandaDate(),
      items,
      total_value: 0,
      reason: `From Requisition ${req.req_number}`,
      urgency: req.urgency,
      is_approved: false,
    }).select().single()
    if (!error && po) {
      await supabase.from('requisitions').update({ status:'Converted to PO', po_id: po.id }).eq('id', req.id)
      setMsg({ type:'success', text:`Purchase Order created from ${req.req_number}. Go to Procurement to add prices and supplier.` })
      loadData()
    }
    setTimeout(() => setMsg(null), 5000)
  }

  const statusColor = { Pending:'badge-amber', Approved:'badge-green', Rejected:'badge-red', 'Converted to PO':'badge-blue' }
  const filtered = tab === 'pending' ? reqs.filter(r => r.status === 'Pending')
    : tab === 'approved' ? reqs.filter(r => r.status === 'Approved')
    : reqs

  return (
    <AppLayout title="Requisitions">
      <div className="section-header">
        <div className="section-icon">📋</div>
        <div className="section-info">
          <h2>Stock Requisitions</h2>
          <p>Request stock replenishment. Low stock items are highlighted automatically.</p>
        </div>
        <button className="btn btn-primary" style={{ marginLeft:'auto' }} onClick={() => setShowForm(true)}>
          + New Requisition
        </button>
      </div>

      {msg && <div className={`alert ${msg.type==='success'?'alert-green':'alert-red'}`}>{msg.text}</div>}

      {/* Low stock alert */}
      {lowStock.length > 0 && (
        <div className="alert alert-red" style={{ marginBottom:16 }}>
          ⚠️ <strong>{lowStock.length} item{lowStock.length>1?'s':''} below reorder level:</strong>{' '}
          {lowStock.map(s => `${s.name} (${parseFloat(s.current_balance).toFixed(2)} ${s.unit} left)`).join(', ')}
          <button className="btn btn-sm btn-danger" style={{ marginLeft:12 }} onClick={() => setShowForm(true)}>
            Raise Requisition
          </button>
        </div>
      )}

      {showForm && (
        <div className="card" style={{ marginBottom:16, border:'2px solid var(--amber)' }}>
          <div className="card-title">New Stock Requisition</div>
          <div className="card-subtitle">Items below reorder level are pre-filled. Adjust quantities as needed.</div>

          <div className="grid-2" style={{ marginBottom:14 }}>
            <div className="form-group"><label className="form-label">Needed By</label><input className="form-input" type="date" value={form.needed_by} onChange={e => setForm(f=>({...f,needed_by:e.target.value}))} /></div>
            <div className="form-group"><label className="form-label">Urgency</label>
              <select className="form-select" value={form.urgency} onChange={e => setForm(f=>({...f,urgency:e.target.value}))}>
                <option>Normal</option><option>Urgent</option><option>Critical</option>
              </select>
            </div>
          </div>

          <div className="table-wrap" style={{ marginBottom:12 }}>
            <table>
              <thead><tr><th>Item</th><th>Current Stock</th><th>Reorder Level</th><th>Qty to Request</th><th>Unit</th><th>Reason</th><th style={{width:32}}></th></tr></thead>
              <tbody>
                {form.items.length === 0 ? (
                  <tr><td colSpan={7} style={{ textAlign:'center', color:'var(--text-muted)', padding:20 }}>All stock levels are above reorder levels. You can still raise a manual requisition.</td></tr>
                ) : form.items.map((item, i) => (
                  <tr key={i} style={{ background: parseFloat(item.current_balance) <= 0 ? 'var(--red-pale)' : 'var(--amber-pale)' }}>
                    <td style={{ fontWeight:600 }}>{item.item_name}</td>
                    <td><span style={{ fontWeight:700, color: parseFloat(item.current_balance)<=0?'var(--red)':'var(--amber)' }}>{parseFloat(item.current_balance).toFixed(2)} {item.unit}</span></td>
                    <td style={{ fontSize:'0.78rem', color:'var(--text-muted)' }}>{item.reorder_level} {item.unit}</td>
                    <td><input className="form-input" style={{ width:90 }} type="number" step="0.1" value={item.qty_requested} onChange={e => setItem(i,'qty_requested',e.target.value)} /></td>
                    <td style={{ fontSize:'0.78rem' }}>{item.unit}</td>
                    <td><input className="form-input" value={item.reason} onChange={e => setItem(i,'reason',e.target.value)} /></td>
                    <td><button className="btn-del-row" title="Remove this item" onClick={() => setForm(f => ({ ...f, items: f.items.filter((_,j) => j !== i) }))}>✕</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="form-group"><label className="form-label">Additional Notes</label><textarea className="form-textarea" style={{ minHeight:60 }} value={form.notes} onChange={e => setForm(f=>({...f,notes:e.target.value}))} placeholder="Any special instructions for procurement..." /></div>
          <div style={{ display:'flex', gap:10 }}>
            <button className="btn btn-primary btn-lg" onClick={handleSave} disabled={saving}>{saving?<><span className="spinner" />Submitting...</>:'📋 Submit Requisition'}</button>
            <button className="btn btn-ghost" onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </div>
      )}

      <div className="tabs">
        <button className={`tab ${tab==='pending'?'active':''}`} onClick={() => setTab('pending')}>⏳ Pending ({reqs.filter(r=>r.status==='Pending').length})</button>
        <button className={`tab ${tab==='approved'?'active':''}`} onClick={() => setTab('approved')}>✅ Approved</button>
        <button className={`tab ${tab==='all'?'active':''}`} onClick={() => setTab('all')}>📚 All</button>
      </div>

      <div className="card">
        {loading ? <div style={{ textAlign:'center', padding:32 }}><span className="spinner dark" /></div>
        : filtered.length === 0 ? <div className="empty-state"><div className="empty-icon">📋</div><h3>No requisitions{tab==='pending'?' pending':''}</h3></div>
        : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Req #</th><th>Date</th><th>Requested By</th><th>Items</th><th>Urgency</th><th>Status</th><th>Approved By</th>{isAdminOrManager && <th>Actions</th>}</tr></thead>
              <tbody>
                {filtered.map(req => (
                  <tr key={req.id}>
                    <td style={{ fontFamily:"'DM Mono',monospace", fontWeight:600 }}>{req.req_number}</td>
                    <td style={{ fontSize:'0.78rem' }}>{new Date(req.req_date).toLocaleDateString('en-UG')}</td>
                    <td>{req.profiles?.full_name || '—'}</td>
                    <td style={{ fontSize:'0.78rem' }}>{req.items?.length || 0} item{req.items?.length !== 1 ? 's' : ''}</td>
                    <td><span className={`badge ${req.urgency==='Critical'?'badge-red':req.urgency==='Urgent'?'badge-amber':'badge-grey'}`}>{req.urgency}</span></td>
                    <td><span className={`badge ${statusColor[req.status]||'badge-grey'}`}>{req.status}</span></td>
                    <td style={{ fontSize:'0.78rem' }}>{req.approver?.full_name || '—'}</td>
                    {isAdminOrManager && (
                      <td>
                        <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
                          {req.status === 'Pending' && (
                            <>
                              <button className="btn btn-secondary btn-sm" style={{ color:'var(--green-mid)' }} onClick={() => handleApprove(req, 'approve')}>✓ Approve</button>
                              <button className="btn btn-danger btn-sm" onClick={() => handleApprove(req, 'reject')}>✗ Reject</button>
                            </>
                          )}
                          {req.status === 'Approved' && !req.po_id && (
                            <button className="btn btn-primary btn-sm" onClick={() => handleConvertToPO(req)}>→ Create PO</button>
                          )}
                          {req.po_id && <span style={{ fontSize:'0.72rem', color:'var(--green-mid)' }}>✅ PO Created</span>}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Reject Modal */}
      {rejectModal && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && setRejectModal(null)}>
          <div className="modal">
            <div className="modal-title">✗ Reject Requisition</div>
            <div className="modal-subtitle">{rejectModal.req_number} — please provide a reason for rejection.</div>
            <div className="form-group">
              <label className="form-label">Reason for Rejection *</label>
              <textarea className="form-textarea" style={{ minHeight:80 }} value={rejectReason} onChange={e=>setRejectReason(e.target.value)} placeholder="e.g. Items already in stock, not budgeted this month..." autoFocus />
            </div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={()=>setRejectModal(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleConfirmReject} disabled={!rejectReason.trim()}>✗ Confirm Rejection</button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  )
}
